import React from 'react';
import { Box, Typography, useTheme } from '@ublend-npm/aulaui-core';
import { FilePresentOutlined } from '@mui/icons-material';
import { postContentParser } from '@components/presentational/Home/widgets/RecentUpdates/postContentParser';

type Props = {
  content: string;
};

const SidebarPinnedPostOverview = ({ content }: Props) => {
  const theme = useTheme();
  const isDark = theme?.palette?.mode == 'dark';
  const { text, imagesCount, filesCount, integrationsCount, linksCount } =
    postContentParser(content);
  const totalAttachments =
    imagesCount + filesCount + integrationsCount + linksCount;
  const lineClamp = totalAttachments > 0 ? 2 : 4;
  const estimatedLines = text
    .split('\n')
    .reduce(
      (total, line) => total + Math.ceil(Math.max(1, line.length) / 55),
      0
    );

  const summaryStyles = {
    border: 0,
    font: 'inherit',
    color: theme.palette.primary.main,
  };

  return (
    <Box
      sx={{
        my: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-line',
          lineHeight: 1.6,
          maxHeight: `${lineClamp * 1.6}em`,
          overflow: 'hidden',
          fontSize: '14px',
          color: isDark ? '#FFFFFFB2' : '#00000099',
        }}
      >
        {text}
      </Typography>
      {estimatedLines > lineClamp && (
        <Box
          sx={{
            ...summaryStyles,
            background: 'none',
            p: 0,
            alignSelf: 'flex-start',
            textDecoration: 'underline',
          }}
        >
          Read more
        </Box>
      )}
      {totalAttachments > 0 && (
        <Box
          aria-label={`View ${totalAttachments} attachment${
            totalAttachments === 1 ? '' : 's'
          } in post`}
          sx={{
            ...summaryStyles,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            p: 1,
            borderRadius: 1,
            bgcolor: theme.palette.background.default,
          }}
        >
          <FilePresentOutlined aria-hidden="true" />
          <Typography
            variant="subtitle1"
            color="inherit"
            sx={{ fontSize: '14px' }}
          >
            {totalAttachments} attachment{totalAttachments === 1 ? '' : 's'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SidebarPinnedPostOverview;
